import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export default function Historico() {
  const { membroAtual, ligaAtual, usuario } = useAuth()
  const [picks, setPicks] = useState([])
  const [pickPendente, setPickPendente] = useState(null)
  const [historicoVidas, setHistoricoVidas] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!membroAtual?.id || !ligaAtual?.id) return
    async function carregar() {
      setLoading(true)

      // Picks já processados (resultado não-null)
      const { data: picksProcessados } = await supabase
        .from('picks')
        .select('*, semanas(numero, tipo, liga_id)')
        .eq('membro_id', membroAtual.id)
        .not('resultado', 'is', null)
        .order('semana_id', { ascending: true })

      // Filtra só picks da liga atual
      const picksDaLiga = (picksProcessados || []).filter(
        p => p.semanas?.liga_id === ligaAtual.id
      )
      setPicks(picksDaLiga)

      // Pick pendente da semana atual (ainda não processado, mas já confirmado)
      const { data: pendente } = await supabase
        .from('picks')
        .select('*, semanas(numero, tipo, liga_id, picks_revelados)')
        .eq('membro_id', membroAtual.id)
        .is('resultado', null)
        .not('time_escolhido', 'is', null)
        .order('semana_id', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (pendente?.semanas?.liga_id === ligaAtual.id) {
        setPickPendente(pendente)
      }

      // historico_vidas para Critério 3 (vidas médias)
      const { data: hv } = await supabase
        .from('historico_vidas')
        .select('vidas, semana_id, semanas(liga_id)')
        .eq('membro_id', membroAtual.id)

      const hvDaLiga = (hv || []).filter(h => h.semanas?.liga_id === ligaAtual.id)
      setHistoricoVidas(hvDaLiga)

      setLoading(false)
    }
    carregar()
  }, [membroAtual?.id, ligaAtual?.id])

  const semanasJogadas = picks.length

  const spreadMedioRegular = (() => {
    // Inclui picks processados + pick pendente com spread congelado
    const todosComSpread = [
      ...picks.filter(p => p.semanas?.tipo === 'regular' && p.spread_congelado !== null),
      ...(pickPendente?.semanas?.tipo === 'regular' && pickPendente?.spread_congelado !== null ? [pickPendente] : [])
    ]
    if (!todosComSpread.length) return null
    return (todosComSpread.reduce((s, p) => s + Number(p.spread_congelado), 0) / todosComSpread.length).toFixed(1)
  })()

  const vidasMedias = (() => {
    if (historicoVidas.length)
      return (historicoVidas.reduce((s, h) => s + h.vidas, 0) / historicoVidas.length).toFixed(1)
    // Sem histórico processado: mostra vidas atuais como posição corrente
    return membroAtual?.vidas_restantes != null ? String(membroAtual.vidas_restantes) : null
  })()

  const resultadoLabel = (r) => {
    if (r === 'win') return { texto: 'Vitória', cor: '#1D9E75', bg: '#EAF3DE' }
    if (r === 'loss') return { texto: 'Derrota', cor: '#A32D2D', bg: '#FCEBEB' }
    if (r === 'draw') return { texto: 'Empate', cor: '#BA7517', bg: '#FAEEDA' }
    if (r === 'nopick') return { texto: 'Sem pick', cor: '#A32D2D', bg: '#FCEBEB' }
    return { texto: '—', cor: '#999', bg: '#f5f5f5' }
  }

  const coracoes = (n) => n > 0 ? '♥'.repeat(n) : '✕'

  return (
    <div>
      {/* Cabeçalho do participante */}
      <div style={s.header}>
        <div style={s.avatar}>{usuario?.primeiro_nome?.[0]}{usuario?.ultimo_nome?.[0]}</div>
        <div>
          <div style={s.nome}>{usuario?.nome_completo}</div>
          <div style={s.sub}>Vidas iniciais: {membroAtual?.vidas_iniciais} · Restantes: {membroAtual?.vidas_restantes} · {semanasJogadas} semana{semanasJogadas !== 1 ? 's' : ''} jogada{semanasJogadas !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Critérios de desempate */}
      <div style={s.criterios}>
        <div style={s.criterio}>
          <div style={s.criterioLabel}>Critério 1</div>
          <div style={s.criterioValor}>{membroAtual?.vidas_restantes} <span style={{ color: '#D85A30' }}>♥</span></div>
          <div style={s.criterioNome}>Vidas restantes</div>
        </div>
        <div style={s.criterio}>
          <div style={s.criterioLabel}>Critério 2</div>
          <div style={s.criterioValor}>{spreadMedioRegular ?? '—'}</div>
          <div style={s.criterioNome}>Spread médio <span style={{ fontSize: 10, color: '#999' }}>(temp. regular)</span></div>
        </div>
        <div style={s.criterio}>
          <div style={s.criterioLabel}>Critério 3</div>
          <div style={s.criterioValor}>{vidasMedias ?? '—'}</div>
          <div style={s.criterioNome}>Vidas médias</div>
        </div>
      </div>

      {/* Aviso spread congelado */}
      <div style={s.aviso}>
        🔒 Spread = valor fixado com os valores do início de cada rodada. Empate = derrota (–1 vida). Critério 2 congelado no fim da temporada regular.
      </div>

      {/* Lista de picks */}
      {loading ? (
        <div style={s.loading}>Carregando...</div>
      ) : picks.length === 0 && !pickPendente ? (
        <div style={s.vazio}>Nenhum pick registrado ainda.</div>
      ) : (
        <table style={s.tabela}>
          <thead>
            <tr>
              <th style={s.th}>Semana</th>
              <th style={s.th}>Time</th>
              <th style={s.th}>Resultado</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Spread 🔒</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Vidas após</th>
            </tr>
          </thead>
          <tbody>
            {picks.map(pick => {
              const r = resultadoLabel(pick.resultado)
              return (
                <tr key={pick.id}>
                  <td style={s.td}>Semana {pick.semanas?.numero}</td>
                  <td style={s.td}>{pick.time_escolhido || '—'}</td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, color: r.cor, background: r.bg }}>{r.texto}</span>
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', color: '#666' }}>
                    {pick.spread_congelado !== null ? `🔒 ${pick.spread_congelado}` : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', color: '#D85A30' }}>
                    {pick.vidas_depois !== null ? coracoes(pick.vidas_depois) : '—'}
                  </td>
                </tr>
              )
            })}
            {/* Pick pendente da semana atual (ainda não processado) */}
            {pickPendente && (
              <tr key="pendente" style={{ opacity: 0.7 }}>
                <td style={s.td}>Semana {pickPendente.semanas?.numero}</td>
                <td style={s.td}>{pickPendente.time_escolhido}</td>
                <td style={s.td}>
                  <span style={{ ...s.badge, color: '#BA7517', background: '#FAEEDA' }}>Aguardando</span>
                </td>
                <td style={{ ...s.td, textAlign: 'right', color: '#666' }}>
                  {pickPendente.spread_congelado !== null ? `🔒 ${pickPendente.spread_congelado}` : '—'}
                </td>
                <td style={{ ...s.td, textAlign: 'right', color: '#bbb' }}>—</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

const s = {
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem', padding: '12px 0' },
  avatar: { width: 42, height: 42, borderRadius: '50%', background: '#FAECE7', color: '#D85A30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, fontSize: 14, flexShrink: 0 },
  nome: { fontWeight: 500, fontSize: 15 },
  sub: { fontSize: 12, color: '#888', marginTop: 2 },
  criterios: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: '1rem' },
  criterio: { background: 'white', border: '0.5px solid #eee', borderRadius: 8, padding: '10px 12px' },
  criterioLabel: { fontSize: 10, color: '#999', marginBottom: 4 },
  criterioValor: { fontSize: 20, fontWeight: 500, marginBottom: 2 },
  criterioNome: { fontSize: 11, color: '#888' },
  aviso: { background: '#EAF3F8', border: '0.5px solid #378ADD', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1A5C9A', marginBottom: '1rem' },
  tabela: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 0', borderBottom: '0.5px solid #eee', fontSize: 11, fontWeight: 500, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em' },
  td: { padding: '10px 0', borderBottom: '0.5px solid #f5f5f5', verticalAlign: 'middle' },
  badge: { fontSize: 11, padding: '3px 8px', borderRadius: 99, fontWeight: 500 },
  loading: { fontSize: 13, color: '#888', padding: '2rem 0', textAlign: 'center' },
  vazio: { fontSize: 13, color: '#888', padding: '2rem 0', textAlign: 'center' },
}
